import { useParams } from 'react-router-dom'
import { useProtocolsList, useProcotol } from '@services/protocols/protocols.hook'
import { formatFlatNumber } from '@services/protocols/protocols.helpers'
import { TextField, Box, CircularProgress, Button, Alert, Stack, Chip } from '@mui/material'
import { usePersistedFormik } from '../hooks/usePersistedFormik'
import AutoSaveIndicator, { AutoSaveStyles } from './AutoSaveIndicator'

function ProtocolGeneralInformation({ isReadOnly }: { isReadOnly: boolean }) {
  const { id } = useParams<{ id: string }>()
  const { updateProtocol } = useProtocolsList()
  const { protocol, refreshProtocol, isLoading } = useProcotol(Number(id))

  const form = usePersistedFormik(
    {
      initialValues: {
        generalComment: protocol?.generalComment ?? ''
      },
      validateOnChange: false,
      validateOnBlur: false,
      onSubmit: async (values) => {
        await updateProtocol.mutate({
          id: Number(id),
          ...values
        })
        refreshProtocol()
      }
    },
    {
      formType: 'protocol',
      protocolId: Number(id),
      autoSaveInterval: 5000,
      showNotifications: false, // No spam notifications
      clearOnSubmit: true
    }
  )

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      {/* WIS Contract indicator */}
      {protocol?.isWisContract && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Chip label='Umowa WIS' color='primary' size='medium' />
        </Box>
      )}

      {/* Unsaved changes indicator */}
      {form.hasUnsavedChanges && (
        <Alert
          severity='warning'
          sx={{ mb: 2 }}
          action={
            <Button size='small' onClick={form.restoreFromSaved} sx={{ mr: 1 }}>
              Odrzuć zmiany
            </Button>
          }
        >
          Formularz zawiera niezapisane zmiany
        </Alert>
      )}

      <TextField
        fullWidth
        label='Lokal'
        disabled
        value={formatFlatNumber(protocol?.flat ?? null)}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label='Kontrakt'
        disabled
        value={protocol?.contractNumber}
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        label='Umowa najmu'
        disabled
        value={protocol?.leaseContract}
        sx={{ mb: 2 }}
      />
      <TextField fullWidth label='Identyfikator' disabled value={protocol?.id} sx={{ mb: 2 }} />
      <TextField fullWidth label='Status' disabled value={protocol?.status} sx={{ mb: 2 }} />

      {/* WIS Consent Status Display - only for WIS contracts */}
      {protocol?.isWisContract && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label='Zgody mieszkańca'
            disabled
            value={
              protocol?.tenantMaintenance === true &&
              protocol?.gardenMaintenanceGroundFloor === true &&
              protocol?.postHandoverDefects24H === true &&
              protocol?.tenantImprovements === true &&
              protocol?.masterKeyConsent === true
                ? 'Wszystkie zgody zaakceptowane'
                : protocol?.tenantMaintenance === null &&
                    protocol?.gardenMaintenanceGroundFloor === null &&
                    protocol?.postHandoverDefects24H === null &&
                    protocol?.tenantImprovements === null &&
                    protocol?.masterKeyConsent === null
                  ? 'Zgody nie zostały jeszcze udzielone'
                  : 'Częściowo zaakceptowane'
            }
            sx={{ mb: 2 }}
          />
        </Box>
      )}
      <TextField
        fullWidth
        label='Komentarz'
        name='generalComment'
        multiline
        rows={8}
        value={form.values.generalComment}
        sx={{ mb: 2 }}
        onChange={form.handleChange}
        disabled={form.isSubmitting}
      />
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <AutoSaveIndicator
          isDirty={form.dirty && !form.hasUnsavedChanges}
          isAutoSaving={false}
          lastSavedTime={form.lastSaved}
        />
        <Button
          variant='contained'
          type='button'
          onClick={form.submitForm}
          disabled={form.isSubmitting || isReadOnly}
        >
          Zapisz
        </Button>
      </Stack>

      <style>{AutoSaveStyles}</style>
    </>
  )
}

export default ProtocolGeneralInformation
